import { apiClient } from './client';
import type { Event, CreateEventDto, UpdateEventDto, EventMember, CreateEventMemberDto } from '@/types/event.types';
import type {
  CreateEventListDto,
  CreateEventListItemDto,
  CreateEventSignupSlotDto,
  EventList,
  EventListItem,
  EventListWithItems,
  EventSignupSlot,
  UpdateEventListDto,
  UpdateEventListItemDto,
  UpdateEventSignupSlotDto,
} from '@om/shared-types';

export class EventService {
  async getEvents(options?: { celebrationId?: number }): Promise<Event[]> {
    const response = await apiClient.get('/events', {
      params: options?.celebrationId ? { celebrationId: options.celebrationId } : undefined,
    });
    return response.data.data || [];
  }

  async getManagedEvents(): Promise<Event[]> {
    const response = await apiClient.get('/events/managed');
    return response.data.data || [];
  }

  async getEvent(id: number): Promise<Event> {
    const response = await apiClient.get(`/events/${id}`);
    // Ensure returned event uses 'title' field
    return response.data.data;
  }

  async createEvent(eventData: CreateEventDto): Promise<Event> {
    // Ensure payload uses 'title' not 'name'
    const response = await apiClient.post('/events', eventData);
    return response.data.data;
  }

  async updateEvent(id: number, eventData: UpdateEventDto): Promise<Event> {
    // Ensure payload uses 'title' not 'name'
    const response = await apiClient.put(`/events/${id}`, eventData);
    return response.data.data;
  }

  async transitionEventStatus(id: number, status: Event['status']): Promise<Event> {
    const response = await apiClient.post(`/events/${id}/status`, { status });
    return response.data.data;
  }

  async deleteEvent(id: number): Promise<void> {
    // Validate the ID before making the API call
    if (!id || id === undefined || isNaN(Number(id)) || id <= 0) {
      throw new Error(`Invalid event ID for deletion: ${id}`);
    }
    
    // Logical delete: DELETE /events/:id
    await apiClient.delete(`/events/${id}`);
  }

  async getEventMembers(eventId: number): Promise<EventMember[]> {
    const response = await apiClient.get(`/events/${eventId}/members`);
    return response.data.data || [];
  }

  async addEventMember(eventId: number, memberData: CreateEventMemberDto): Promise<EventMember> {
    const response = await apiClient.post(`/events/${eventId}/members`, memberData);
    return response.data.data;
  }

  async removeEventMember(eventId: number, memberId: number): Promise<void> {
    await apiClient.delete(`/events/${eventId}/members/${memberId}`);
  }

  async getEventSignupSlots(eventId: number): Promise<EventSignupSlot[]> {
    const response = await apiClient.get(`/events/${eventId}/sign-up-slots`);
    return response.data.data || [];
  }

  async createEventSignupSlot(eventId: number, payload: CreateEventSignupSlotDto): Promise<EventSignupSlot> {
    const response = await apiClient.post(`/events/${eventId}/sign-up-slots`, payload);
    return response.data.data;
  }

  async updateEventSignupSlot(eventId: number, slotId: number, payload: UpdateEventSignupSlotDto): Promise<EventSignupSlot> {
    const response = await apiClient.put(`/events/${eventId}/sign-up-slots/${slotId}`, payload);
    return response.data.data;
  }

  async releaseEventSignupSlot(eventId: number, slotId: number): Promise<EventSignupSlot> {
    const response = await apiClient.post(`/events/${eventId}/sign-up-slots/${slotId}/release`);
    return response.data.data;
  }

  async fulfillEventSignupSlot(eventId: number, slotId: number): Promise<EventSignupSlot> {
    const response = await apiClient.post(`/events/${eventId}/sign-up-slots/${slotId}/fulfill`);
    return response.data.data;
  }

  async reassignEventSignupSlot(eventId: number, slotId: number, claimedByName: string): Promise<EventSignupSlot> {
    const response = await apiClient.post(`/events/${eventId}/sign-up-slots/${slotId}/reassign`, { claimed_by_name: claimedByName });
    return response.data.data;
  }

  async deleteEventSignupSlot(eventId: number, slotId: number): Promise<void> {
    await apiClient.delete(`/events/${eventId}/sign-up-slots/${slotId}`);
  }

  async getEventLists(eventId: number): Promise<EventListWithItems[]> {
    const response = await apiClient.get(`/events/${eventId}/lists`);
    return response.data.data || [];
  }

  async createEventList(eventId: number, payload: CreateEventListDto): Promise<EventList> {
    const response = await apiClient.post(`/events/${eventId}/lists`, payload);
    return response.data.data;
  }

  async updateEventList(eventId: number, listId: number, payload: UpdateEventListDto): Promise<EventList> {
    const response = await apiClient.put(`/events/${eventId}/lists/${listId}`, payload);
    return response.data.data;
  }

  async deleteEventList(eventId: number, listId: number): Promise<void> {
    await apiClient.delete(`/events/${eventId}/lists/${listId}`);
  }

  async addEventListItem(eventId: number, listId: number, payload: CreateEventListItemDto): Promise<EventListItem> {
    const response = await apiClient.post(`/events/${eventId}/lists/${listId}/items`, payload);
    return response.data.data;
  }

  async updateEventListItem(eventId: number, listId: number, itemId: number, payload: UpdateEventListItemDto): Promise<EventListItem> {
    const response = await apiClient.put(`/events/${eventId}/lists/${listId}/items/${itemId}`, payload);
    return response.data.data;
  }

  async releaseEventListItem(eventId: number, listId: number, itemId: number): Promise<EventListItem> {
    const response = await apiClient.post(`/events/${eventId}/lists/${listId}/items/${itemId}/release`);
    return response.data.data;
  }

  async fulfillEventListItem(eventId: number, listId: number, itemId: number): Promise<EventListItem> {
    const response = await apiClient.post(`/events/${eventId}/lists/${listId}/items/${itemId}/fulfill`);
    return response.data.data;
  }

  async reassignEventListItem(eventId: number, listId: number, itemId: number, claimedByName: string): Promise<EventListItem> {
    const response = await apiClient.post(`/events/${eventId}/lists/${listId}/items/${itemId}/reassign`, { claimed_by_name: claimedByName });
    return response.data.data;
  }

  async deleteEventListItem(eventId: number, listId: number, itemId: number): Promise<void> {
    await apiClient.delete(`/events/${eventId}/lists/${listId}/items/${itemId}`);
  }
}

export const eventService = new EventService();
